/**
 * What a canvas card reference means in a browser.
 *
 * Kept as pure functions, apart from the component that renders them, because
 * this is the part with a failure mode worth guarding: a reference the console
 * cannot place must become a card that says so, never a card that silently
 * disappears or renders an empty box. A dropped card reads as "the board lost
 * my work", and the board file on the desktop still holds it.
 *
 * There are only two reference kinds to place. The runtime already rewrites a
 * shared `file` node to its `sharedAs` target before calling the host on a
 * collaborative board, so the `file` branch here is what remains: a card
 * pointing at a path on the machine that authored the board, which a browser
 * has no way to read. `sharedAs` is honoured anyway -- it costs one branch and
 * it is the difference between an honest card and a wrong one if that rewrite
 * ever stops happening upstream.
 */

import { isCollabUri, parseCollabUri } from '@nimbalyst/collab-protocol';
import type { CanvasCardReference } from '@nimbalyst/collab-bundle/canvas';

import type { WebCanvasCardScope } from './webCanvasCardScope';

export type WebCanvasCardResolution =
  | { kind: 'document'; orgId: string; projectId: string; documentId: string }
  | { kind: 'unavailable'; reason: WebCanvasCardUnavailableReason };

export type WebCanvasCardUnavailableReason =
  /** A workspace file with no shared counterpart. There is no filesystem here. */
  | { code: 'local-file'; path: string }
  /** No Shared Docs scope is open, so no room can be named. */
  | { code: 'no-project' }
  /** The card names an org this console is not currently signed in to. */
  | { code: 'other-team'; orgId: string }
  /** The card is pinned to a revision, and this console cannot read history. */
  | { code: 'pinned-revision'; revisionId: string }
  /** The card carries something that is not a document URI at all. */
  | { code: 'unresolvable'; target: string };

/**
 * Both spellings a board can carry, matching the desktop card host exactly.
 *
 * `nimbalyst://doc/<orgId>/<documentId>` is what `CanvasDocumentTarget` types
 * and what the converter writes; `collab://org:<orgId>:doc:<documentId>` is the
 * tab-identity form the rest of the app uses, and boards authored through a
 * share flow can carry it. A host that understood only one of them would open
 * some shared cards and blank others.
 */
export function parseCanvasDocumentUri(
  uri: string,
): { orgId: string; documentId: string } | null {
  if (isCollabUri(uri)) {
    try {
      return parseCollabUri(uri);
    } catch {
      return null;
    }
  }
  try {
    const url = new URL(uri);
    if (url.protocol !== 'nimbalyst:' || url.hostname !== 'doc') return null;
    const [encodedOrgId, ...documentParts] = url.pathname.replace(/^\/+/, '').split('/');
    if (!encodedOrgId || documentParts.length === 0) return null;
    const documentId = decodeURIComponent(documentParts.join('/'));
    if (!documentId) return null;
    return { orgId: decodeURIComponent(encodedOrgId), documentId };
  } catch {
    return null;
  }
}

/** The reference as a short string, for the label under an unavailable card. */
export function canvasCardTarget(reference: CanvasCardReference): string {
  return reference.kind === 'file' ? reference.path : reference.uri;
}

export function resolveWebCanvasCard(
  reference: CanvasCardReference,
  scope: WebCanvasCardScope | null,
): WebCanvasCardResolution {
  /*
   * A pinned card is checked before anything else, and it refuses.
   *
   * This console has no revision client: there is no `listRevisions` and no
   * `loadRevision` anywhere in it, so there is no way to fetch the bytes a
   * pinned card names. The only alternative to refusing would be to mount the
   * live room -- which is precisely the bug this branch exists to prevent. The
   * card would be styled as history, labelled "v3", and render head, and two
   * cards pinned to two revisions would be the same document twice with
   * nothing on screen to say so.
   *
   * `local-file` is the precedent: name what the card points at, say plainly
   * why it is not here, and do not fake it.
   */
  const revisionId = pinnedRevisionId(reference);
  if (revisionId !== null) {
    return { kind: 'unavailable', reason: { code: 'pinned-revision', revisionId } };
  }
  if (reference.kind === 'file') {
    const shared = reference.sharedAs;
    return shared
      ? resolveDocumentUri(shared.uri, scope)
      : { kind: 'unavailable', reason: { code: 'local-file', path: reference.path } };
  }
  return resolveDocumentUri(reference.uri, scope);
}

/**
 * The revision this card is pinned to, or null for a card that follows head.
 *
 * Restates the runtime's `resolveCanvasCardRevision` rule -- both shapes that
 * can carry a pin, and "absent, empty, or not a string" all meaning head --
 * rather than importing it, because this file is the console's whole
 * understanding of a reference and has no other runtime dependency. If the two
 * ever drift, the desktop's rule is authoritative.
 */
function pinnedRevisionId(reference: CanvasCardReference): string | null {
  const raw =
    reference.kind === 'doc' ? reference.revisionId : reference.sharedAs?.revisionId;
  return typeof raw === 'string' && raw !== '' ? raw : null;
}

function resolveDocumentUri(
  target: string,
  scope: WebCanvasCardScope | null,
): WebCanvasCardResolution {
  const parsed = parseCanvasDocumentUri(target);
  if (!parsed) {
    return { kind: 'unavailable', reason: { code: 'unresolvable', target } };
  }
  if (!scope) {
    return { kind: 'unavailable', reason: { code: 'no-project' } };
  }
  if (parsed.orgId !== scope.orgId) {
    return { kind: 'unavailable', reason: { code: 'other-team', orgId: parsed.orgId } };
  }
  return {
    kind: 'document',
    orgId: parsed.orgId,
    projectId: scope.projectId,
    documentId: parsed.documentId,
  };
}
